/**
 * TanStack Query hooks for `branding`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	get,
	update,
	replaceLogo,
	savePortalName,
	brandingQueryKeys,
	type BrandingSettings,
	type UpdateBrandingBody,
} from "./api";

export function useBrandingSettings(): UseQueryResult<BrandingSettings, Error> {
	return useQuery({
		queryKey: brandingQueryKeys.settings(),
		queryFn: get,
		staleTime: 30_000,
	});
}

export function useBrandingUpdate(): UseMutationResult<
	BrandingSettings,
	Error,
	UpdateBrandingBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: update,
		onSuccess: (fresh) => {
			qc.setQueryData(brandingQueryKeys.settings(), fresh);
			qc.invalidateQueries({ queryKey: brandingQueryKeys.all });
		},
	});
}

export function useBrandingReplaceLogo(): UseMutationResult<
	BrandingSettings,
	Error,
	File
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: replaceLogo,
		onSuccess: (fresh) => {
			qc.setQueryData(brandingQueryKeys.settings(), fresh);
			qc.invalidateQueries({ queryKey: brandingQueryKeys.all });
		},
	});
}

export function useBrandingRemoveLogo(): UseMutationResult<
	BrandingSettings,
	Error,
	void
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => update({ logo_r2_key: null }),
		onSuccess: (fresh) => {
			qc.setQueryData(brandingQueryKeys.settings(), fresh);
			qc.invalidateQueries({ queryKey: brandingQueryKeys.all });
		},
	});
}

/** Save portal name to local storage (no backend column for this yet). */
export function usePortalName(): (name: string) => void {
	const qc = useQueryClient();
	return (name: string) => {
		savePortalName(name);
		// Nudge the settings query so subscribers re-render with the new name.
		const current = qc.getQueryData<BrandingSettings>(brandingQueryKeys.settings());
		if (current) {
			qc.setQueryData(brandingQueryKeys.settings(), {
				...current,
				portal_name: name || undefined,
			});
		}
	};
}
